import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  styled,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Close as CloseIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import DialogTitle from '../atoms/DialogTitle';
import Input from '../atoms/Input';
import InputLabel from '../atoms/InputLabel';
import RdButton from '../atoms/RdButton';
import LocationDropdown from '../molecules/LocationDropdown';

/**
 * Props for the DesignNameDialog component
 */
export interface DesignNameDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: DesignNameData) => void;
  initialName?: string;
  designType?: string;
  mode?: 'duplicate' | 'rename' | 'new';
  showWorkplaceLocation?: boolean;
  organizationId?: string;
  initialLocationId?: string;
}

/**
 * Data returned from the dialog
 */
export interface DesignNameData {
  name: string;
  type?: string;
  workplaceLocation?: string;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '6px',
    minWidth: '520px',
    maxWidth: '520px',
    backgroundColor: theme.palette.background.paper,
    backgroundImage: 'none',
    boxShadow: '0px 4px 50px rgba(17, 17, 17, 0.10)',
    border: `0.6px solid ${theme.palette.divider}`,
  }
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: '0 36px 24px 36px',
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: '0 36px 36px 36px',
  gap: '5px',
}));

const DescriptionText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '14px',
  fontFamily: 'Open Sans',
  fontWeight: 400,
  lineHeight: '21px',
  marginBottom: '24px',
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  '& .MuiDialogTitle-root': {
    padding: '36px 36px 24px 36px',
    fontSize: '20px',
    fontWeight: 600,
    fontFamily: 'Montserrat',
    lineHeight: '20px',
  }
}));

const InputRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  marginBottom: '16px',
  alignItems: 'stretch',
}));

const InputWrapper = styled(Box)<{ disabled?: boolean }>(({ theme, disabled }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  opacity: disabled ? 0.6 : 1,
}));


/**
 * DesignNameDialog component - A dialog for creating, duplicating, or renaming designs
 *
 * This component provides a clean, accessible dialog for managing design names
 * with optional workplace location field and disabled type field.
 *
 * @param props - Component props
 * @returns DesignNameDialog component
 */
const DesignNameDialog: React.FC<DesignNameDialogProps> = ({
  open,
  onClose,
  onConfirm,
  initialName = '',
  designType = 'Conference Room',
  mode = 'duplicate',
  showWorkplaceLocation = false,
  organizationId = '',
  initialLocationId = '',
}) => {
  const [designName, setDesignName] = useState(initialName);
  const [workplaceLocationId, setWorkplaceLocationId] = useState(initialLocationId);
  const [locationExpanded, setLocationExpanded] = useState(showWorkplaceLocation || !!initialLocationId);
  const [nameError, setNameError] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setDesignName(initialName);
      setWorkplaceLocationId(initialLocationId);
      setLocationExpanded(showWorkplaceLocation || !!initialLocationId);
      setNameError(false);
      setHasAttemptedSubmit(false);
    }
  }, [open, initialName, initialLocationId, showWorkplaceLocation]);

  // Handle close with state cleanup
  const handleClose = () => {
    // Clear all state when closing
    setDesignName('');
    setWorkplaceLocationId('');
    setLocationExpanded(false);
    setNameError(false);
    setHasAttemptedSubmit(false);
    onClose();
  };

  const getTitle = () => {
    switch (mode) {
      case 'duplicate':
        return 'Duplicate design';
      case 'rename':
        return 'Rename design';
      case 'new':
        return "Let's get started";
      default:
        return 'Design name';
    }
  };

  const getDescription = () => {
    switch (mode) {
      case 'duplicate':
        return 'Create a copy of your design file. Rename it or change its location as needed before saving.';
      case 'rename':
        return 'Change the name of your design file.';
      case 'new':
        return 'Welcome! To get started, give your design a name.';
      default:
        return '';
    }
  };

  const getButtonText = () => {
    switch (mode) {
      case 'duplicate':
        return 'Create copy';
      case 'new':
      case 'rename':
        return 'Continue';
      default:
        return 'Confirm';
    }
  };

  const getSecondaryButtonText = () => {
    return mode === 'new' ? 'Skip' : 'Cancel';
  };

  const validateForm = (): boolean => {
    const trimmedName = designName.trim();

    // Check if name is empty
    if (!trimmedName) {
      setNameError(true);
      return false;
    }

    // Check for invalid characters (optional - you can customize this)
    const invalidChars = /[<>:"/\\|?*]/g;
    if (invalidChars.test(trimmedName)) {
      setNameError(true);
      return false;
    }

    // Check max length (optional - customize as needed)
    if (trimmedName.length > 100) {
      setNameError(true);
      return false;
    }

    setNameError(false);
    return true;
  };

  const handleConfirm = () => {
    setHasAttemptedSubmit(true);

    // Validate the form
    if (!validateForm()) {
      return; // Don't proceed if validation fails
    }

    const data: DesignNameData = {
      name: designName.trim(), // Use trimmed name
      type: designType,
      workplaceLocation: workplaceLocationId || '' // Always include location, even if empty
    };

    onConfirm(data);
  };

  const handleSecondaryAction = () => {
    if (mode === 'new') {
      // For Skip in new mode, proceed without name
      // Still call onConfirm to trigger navigation, but with empty data
      onConfirm({ name: '', type: designType });
    } else {
      // For Cancel in other modes, just close with cleanup
      handleClose();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && designName.trim()) {
      handleConfirm();
    } else if (event.key === 'Escape') {
      handleClose();
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={(event, reason) => {
        // Only close on escape key or explicit close button, not backdrop click
        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}
      onKeyDown={handleKeyDown}
      maxWidth={false}
      disableEscapeKeyDown={false}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)', // For Safari support
        }
      }}
    >
      <StyledDialogTitle onClose={handleClose}>
        {getTitle()}
      </StyledDialogTitle>

      <StyledDialogContent>
        <DescriptionText>
          {getDescription()}
        </DescriptionText>

        <InputRow>
          <InputWrapper>
            <InputLabel required>Design name</InputLabel>
            <Input
              fullWidth
              value={designName}
              onChange={(e) => {
                setDesignName(e.target.value);
                // Clear error when user starts typing
                if (hasAttemptedSubmit && e.target.value.trim()) {
                  setNameError(false);
                }
              }}
              placeholder="Enter design name"
              autoFocus
              autoComplete="off"
              required
              error={nameError && hasAttemptedSubmit}
              helperText={
                nameError && hasAttemptedSubmit
                  ? !designName.trim()
                    ? 'Design name is required'
                    : designName.trim().length > 100
                    ? 'Design name must be less than 100 characters'
                    : 'Design name contains invalid characters'
                  : ''
              }
            />
          </InputWrapper>

          <InputWrapper>
            <InputLabel>Type</InputLabel>
            <Input
              fullWidth
              value={designType}
              disabled
              InputProps={{
                readOnly: true,
              }}
            />
          </InputWrapper>
        </InputRow>

        {/* Add Workplace location (collapsible) */}
        <Box sx={{ marginBottom: '20px' }}>
          <Box
            onClick={() => setLocationExpanded(!locationExpanded)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              mb: locationExpanded ? 2 : 0,
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            <ChevronRightIcon
              sx={{
                fontSize: 20,
                padding: 0,
                marginLeft: '-5px',
                transform: locationExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
            <InputLabel
              optional
              sx={{
                marginBottom: 0,
                marginTop: '6px',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              Add Workplace location
            </InputLabel>
          </Box>

          {/* Expandable location section */}
          {locationExpanded && (
            <Box sx={{ mt: 2 }}>
              <InputLabel>Location</InputLabel>
              <LocationDropdown
                organizationId={organizationId || ''}
                value={workplaceLocationId}
                onChange={setWorkplaceLocationId}
                placeholder="No location selected (optional)"
              />
            </Box>
          )}
        </Box>
      </StyledDialogContent>

      <StyledDialogActions>
        <RdButton
          onClick={handleSecondaryAction}
          sx={{ minWidth: '140px' }}
        >
          {getSecondaryButtonText()}
        </RdButton>
        <RdButton
          buttonType="primary"
          onClick={handleConfirm}
          disabled={!designName.trim()}
          sx={{ flex: 1 }}
        >
          {getButtonText()}
        </RdButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default DesignNameDialog;